import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useVersionCheck, DevCacheUtils } from './utils/cacheUtils';
import { featureFlags } from './services/featureFlags';
import { FrontendFeatureKey } from './utils/frontendFeatures';
import { useFeature } from './hooks/useFeature';
import LoginPage from './components/LoginPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import RecordingsPage from './components/RecordingsPage';
import RecordingDetailPage from './components/RecordingDetailPage';
import SettingsPage from './components/SettingsPage';
import TemplatesManager from './components/TemplatesManager';
import TemplateSuggestionsPage from './components/TemplateSuggestionsPage';
import UpdatesPage from './components/UpdatesPage';
import UpdateDetailPage from './components/UpdateDetailPage';
import UserSettings from './components/UserSettings';
import VersionFooter from './components/VersionFooter';
import RecorderOverlay from './components/RecorderOverlay';
import useRecorderStatus from './hooks/useRecorderStatus';
import AppShell from './components/AppShell';
import { logger } from './utils/logger';

// Automatische update notificatie component
const AutoUpdateNotification: React.FC<{
    onUpdate: () => void;
    countdownSeconds?: number;
}> = ({ onUpdate, countdownSeconds = 8 }) => {
    const [countdown, setCountdown] = useState(countdownSeconds);
    const [isUpdating, setIsUpdating] = useState(false);
    const recorderStatus = useRecorderStatus();
    // Bijwerken betekent herladen; tijdens een opname/upload zou dat de opname
    // weggooien. De aftelling pauzeert dan en loopt daarna gewoon verder.
    const recorderBusy = recorderStatus.isRecording || recorderStatus.isPaused || recorderStatus.isUploading;

    useEffect(() => {
        if (recorderBusy) {
            return;
        }

        // Start countdown
        const interval = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    setIsUpdating(true);
                    clearInterval(interval);
                    // Automatisch updaten na countdown
                    setTimeout(() => {
                        onUpdate();
                    }, 500);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [onUpdate, recorderBusy]);

    const handleManualUpdate = () => {
        setIsUpdating(true);
        onUpdate();
    };

    return (
        <div className="bg-green-600 text-white p-3 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    {isUpdating ? (
                        <svg className="animate-spin w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                    )}
                    <span className="font-medium">
                        {isUpdating
                            ? 'Bijwerken naar nieuwe versie...'
                            : recorderBusy
                                ? 'Nieuwe versie beschikbaar — we werken bij zodra je opname klaar is'
                                : `Nieuwe versie beschikbaar! Automatisch bijwerken in ${countdown}s`
                        }
                    </span>
                </div>
                {!isUpdating && !recorderBusy && (
                    <button
                        onClick={handleManualUpdate}
                        className="bg-white text-green-600 px-3 py-1 rounded text-sm font-medium hover:bg-green-50 transition-colors"
                    >
                        Nu bijwerken
                    </button>
                )}
            </div>
        </div>
    );
};

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isLoggedIn, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-lg font-medium">Laden...</span>
                </div>
            </div>
        );
    }

    return isLoggedIn ? <>{children}</> : <Navigate to="/login" replace />;
};

// Feature-gated route: only renders children if the given feature key is enabled.
// Defaults per feature staan centraal in FRONTEND_FEATURE_DEFAULTS.
const FeatureRoute: React.FC<{ feature: FrontendFeatureKey; children: React.ReactNode }> = ({ feature, children }) => {
    const enabled = useFeature(feature);
    return enabled ? <>{children}</> : <Navigate to="/recordings" replace />;
};

// Public Route component (redirect to recordings if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isLoggedIn, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-lg font-medium">Laden...</span>
                </div>
            </div>
        );
    }

    return isLoggedIn ? <Navigate to="/recordings" replace /> : <>{children}</>;
};

// Development panel component
const DevelopmentPanel: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);

    if (process.env.NODE_ENV !== 'development') {
        return null;
    }

    const handleClearCache = async () => {
        await DevCacheUtils.clearAllDevelopmentData();
        alert('Development cache en data gewist! De pagina wordt herladen.');
        window.location.reload();
    };

    return (
        <>
            {/* Toggle button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed bottom-4 left-4 bg-yellow-500 text-white p-2 rounded-full shadow-lg z-50 hover:bg-yellow-600 transition-colors"
                title="Development Tools"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            </button>

            {/* Panel */}
            {isOpen && (
                <div className="fixed bottom-16 left-4 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-50 w-80">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-800">Development Tools</h3>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="space-y-2">
                        <button
                            onClick={handleClearCache}
                            className="w-full bg-red-500 text-white px-3 py-2 rounded text-sm hover:bg-red-600 transition-colors"
                        >
                            🗑️ Wis Alle Cache & Data
                        </button>

                        <button
                            onClick={() => window.location.reload()}
                            className="w-full bg-blue-500 text-white px-3 py-2 rounded text-sm hover:bg-blue-600 transition-colors"
                        >
                            🔄 Hard Refresh
                        </button>

                        <button
                            onClick={() => {
                                const debugInfo = featureFlags.getDebugInfo();
                                logger.log('🚀 Feature Flags Debug Info:', debugInfo);
                            }}
                            className="w-full bg-purple-500 text-white px-3 py-2 rounded text-sm hover:bg-purple-600 transition-colors"
                        >
                            🚀 Show Feature Flags
                        </button>

                        <div className="text-xs text-gray-600 mt-2">
                            <p>• Service Worker: Uitgeschakeld</p>
                            <p>• Cache Busting: Actief</p>
                            <p>• Version Check: Uitgeschakeld</p>
                            <p>• Feature Flags: notizyFeatures.help()</p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const ProtectedAppLayout: React.FC<{ banner?: React.ReactNode }> = ({ banner }) => (
    <AppShell banner={banner}>
        <RecorderOverlay />
        <Outlet />
    </AppShell>
);

// Publieke pagina's (login e.d.) hebben geen AppShell; toon de banner daar in de normale flow
const PUBLIC_PATHS = ['/login', '/reset-password', '/settings'];

const PublicUpdateBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    if (!children || !PUBLIC_PATHS.includes(location.pathname)) {
        return null;
    }
    return <>{children}</>;
};

function App() {
    // Check elke 10 minuten voor updates (was 5 minuten)
    const { updateAvailable, applyUpdate } = useVersionCheck(10 * 60 * 1000);

    // Setup development mode en feature flags wanneer de app start
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            DevCacheUtils.setupDevelopmentMode();
        }

        // Initialiseer feature flags (console commands worden automatisch geregistreerd)
        featureFlags.getDebugInfo(); // Dit zorgt ervoor dat de service wordt geïnitialiseerd
    }, []);

    const updateNotification = updateAvailable ? (
        <AutoUpdateNotification
            onUpdate={applyUpdate}
            countdownSeconds={8}
        />
    ) : null;

    return (
        <AuthProvider>
            <Router>
                <div className="App">
                    <PublicUpdateBanner>{updateNotification}</PublicUpdateBanner>

                    <div>
                        <Routes>
                            {/* Public routes */}
                            <Route
                                path="/login"
                                element={
                                    <PublicRoute>
                                        <LoginPage />
                                    </PublicRoute>
                                }
                            />

                            {/* Password reset page (public, no redirect if logged in) */}
                            <Route
                                path="/reset-password"
                                element={<ResetPasswordPage />}
                            />

                            {/* Server settings: only accessible when not logged in */}
                            <Route
                                path="/settings"
                                element={
                                    <PublicRoute>
                                        <SettingsPage />
                                    </PublicRoute>
                                }
                            />

                            {/* Protected routes */}
                            <Route
                                element={
                                    <ProtectedRoute>
                                        <ProtectedAppLayout banner={updateNotification} />
                                    </ProtectedRoute>
                                }
                            >
                                <Route
                                    path="/recordings"
                                    element={<RecordingsPage />}
                                />
                                <Route
                                    path="/recording/:recordingId"
                                    element={<RecordingDetailPage />}
                                />
                                <Route
                                    path="/templates/:templateId?"
                                    element={
                                        <FeatureRoute feature="template_editing">
                                            <TemplatesManager />
                                        </FeatureRoute>
                                    }
                                />
                                <Route
                                    path="/template-suggestions"
                                    element={
                                        <FeatureRoute feature="template_suggestions">
                                            <TemplateSuggestionsPage />
                                        </FeatureRoute>
                                    }
                                />
                                <Route
                                    path="/updates"
                                    element={<UpdatesPage />}
                                />
                                <Route
                                    path="/updates/:updateSlug"
                                    element={<UpdateDetailPage />}
                                />
                                <Route
                                    path="/user-settings"
                                    element={<UserSettings />}
                                />
                            </Route>

                            {/* Redirect root to recordings */}
                            <Route path="/" element={<Navigate to="/recordings" replace />} />

                            {/* Catch all - redirect to recordings */}
                            <Route path="*" element={<Navigate to="/recordings" replace />} />
                        </Routes>
                    </div>

                    {/* Versie footer - altijd zichtbaar */}
                    <VersionFooter />

                    {/* Development panel - alleen in development */}
                    <DevelopmentPanel />
                </div>
            </Router>
        </AuthProvider>
    );
}

export default App; 
